/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';
import { type VehicleProps } from '../components/types';
import { type AcesVehicleAttributes } from './types';
import { useFeatureFlag } from '@/features/globalConfig';
import environmentConfig from '@/config/environment';

const V1_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/vehicles/search-by-vin-or-license-plate`;
const V2_ELASTIC_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v2/vehicles/search-by-vin-or-license-plate`;

type V2DecoderVehicle = {
  vin: string;
  vehicleName: string;
  modelName: string;
  acesVehicleAttributes: AcesVehicleAttributes;
};

type V2DecoderResponse = V2DecoderVehicle[];

type V2VehicleData = Array<{
  vin: string;
  vehicleDisplayName: string;
  acesVehicleAttributes: AcesVehicleAttributes;
}>;

// ---- VIN Decoder ----

type VinDecoderV2Options = {
  locale: Locale;
  vinNumber: string | undefined;
  vehicleElasticEnabled: boolean;
};

const { useData: useVinDecoderV2Data } = createQuery<V2VehicleData, VinDecoderV2Options>(
  'vinV2',
  async (options, axiosInstance) => getVehicleByVinV2(options, axiosInstance),
);

const getVehicleByVinV2 = async (
  options: VinDecoderV2Options,
  axiosInstance?: AxiosInstance,
): Promise<V2VehicleData> => {
  const { locale, vinNumber, vehicleElasticEnabled } = options;

  if (!vinNumber) {
    throw new Error('vinNumber is required');
  }

  const country = getCountryFromLocale(locale);
  const url = vehicleElasticEnabled ? V2_ELASTIC_URL : V1_URL;

  const response = await getAxios(axiosInstance).get<V2DecoderResponse>(url, {
    params: {
      vin: vinNumber,
      callingClient: 'DIY-DESKTOP',
      country,
    },
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return response.data.map(({ vin, vehicleName, modelName, acesVehicleAttributes }) => ({
    vin,
    vehicleDisplayName: vehicleName,
    acesVehicleAttributes: { ...acesVehicleAttributes, modelName },
  }));
};

type UseVinDecoderV2Options = {
  vinNumber?: string;
  onSuccess?: (vehicles: VehicleProps[]) => void;
  enabled?: boolean;
};

export const useVinDecoderV2 = ({
  vinNumber,
  onSuccess,
  enabled = false,
}: UseVinDecoderV2Options) => {
  const locale = useLocale();
  const vehicleElasticEnabled = useFeatureFlag('VEHICLE_ELASTIC_ENABLED') === 'true';

  const queryResult = useVinDecoderV2Data({
    enabled: Boolean(vinNumber) && enabled,
    locale,
    vinNumber,
    vehicleElasticEnabled,
    gcTime: 0,
  });

  useQueryResultOnSuccess(queryResult, onSuccess, true);

  return queryResult;
};

// ---- License Plate Decoder ----

type LicensePlateDecoderV2Options = {
  locale: Locale;
  licensePlateNumber: string | undefined;
  licensePlateState: string | undefined;
  vehicleElasticEnabled: boolean;
};

const { useData: useLicensePlateDecoderV2Data } = createQuery<
  V2VehicleData,
  LicensePlateDecoderV2Options
>('licensePlateV2', async (options, axiosInstance) =>
  getVehicleByLicensePlateV2(options, axiosInstance),
);

const getVehicleByLicensePlateV2 = async (
  options: LicensePlateDecoderV2Options,
  axiosInstance?: AxiosInstance,
): Promise<V2VehicleData> => {
  const { locale, licensePlateNumber, licensePlateState, vehicleElasticEnabled } = options;

  if (!licensePlateNumber) {
    throw new Error('licensePlateNumber is required');
  } else if (!licensePlateState) {
    throw new Error('licensePlateState is required');
  }

  const country = getCountryFromLocale(locale);
  const url = vehicleElasticEnabled ? V2_ELASTIC_URL : V1_URL;

  const response = await getAxios(axiosInstance).get<V2DecoderResponse>(url, {
    params: {
      licensePlate: licensePlateNumber,
      state: licensePlateState,
      callingClient: 'DIY-DESKTOP',
      country,
    },
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return response.data.map(({ vin, vehicleName, modelName, acesVehicleAttributes }) => ({
    vin,
    vehicleDisplayName: vehicleName,
    acesVehicleAttributes: { ...acesVehicleAttributes, modelName },
  }));
};

type UseLicensePlateDecoderV2Options = {
  licensePlateNumber?: string;
  licensePlateState?: string;
  onSuccess?: (vehicles: VehicleProps[]) => void;
  enabled?: boolean;
};

export const useLicensePlateDecoderV2 = ({
  licensePlateNumber,
  licensePlateState,
  onSuccess,
  enabled = false,
}: UseLicensePlateDecoderV2Options) => {
  const locale = useLocale();
  const vehicleElasticEnabled = useFeatureFlag('VEHICLE_ELASTIC_ENABLED') === 'true';

  const queryResult = useLicensePlateDecoderV2Data({
    enabled: Boolean(licensePlateNumber && licensePlateState && enabled),
    locale,
    licensePlateNumber,
    licensePlateState,
    vehicleElasticEnabled,
    gcTime: 0,
  });

  useQueryResultOnSuccess(queryResult, onSuccess, true);

  return queryResult;
};
